import {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';

export interface WorkflowCapabilityCatalogEntry {
  key: WorkflowCapabilityReference['key'];
  module: string;
  lifecycle: string;
  authorizationPolicy: string;
  input: { hash: string } | null;
  output: { hash: string } | null;
  contractHash: string;
  dependencies: WorkflowCapabilityReference[];
  requiredContext: string[];
  unavailableReason: string | null;
  ownerStoryId: number | null;
}

/** Exact formats supported by the existing pinned-reference writer. Legacy upgrades are deliberate. */
export function supportsWorkflowCapabilities(version: string | null | undefined): boolean {
  return ['1.4', '1.5', '1.6', '1.7', '1.8', '1.9', '1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(version ?? '');
}

/** Keep unsupported saved wire content intact rather than editing an incomplete projection. */
export function capabilityReferencesShapeError(value: unknown): string | null {
  if (value == null) return null;
  const object = (value: unknown, keys: readonly string[]): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).every(key => keys.includes(key));
  if (!Array.isArray(value)) return 'The saved dependency list has an unsupported format. Its original content is retained read-only.';
  const identities = new Set<string>();
  for (const reference of value) {
    if (!object(reference, ['key', 'inputSchemaHash', 'outputSchemaHash', 'contractHash']) ||
      !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every(key => typeof reference[key] === 'string' && !!reference[key]))
      return 'A saved dependency contains unsupported wire settings. Its original content is retained read-only.';
    const key = reference['key'];
    if (!object(key, ['provider', 'kind', 'code', 'version']) || !['provider', 'kind', 'code'].every(name => typeof key[name] === 'string' && !!key[name]) ||
      !Number.isSafeInteger(key['version']) || Number(key['version']) < 1)
      return 'A saved dependency identity is unsupported. Its original content is retained read-only.';
    const identity = JSON.stringify([key['provider'], key['kind'], key['code'], key['version']]);
    if (identities.has(identity)) return 'The saved dependency list contains ambiguous duplicate identities. Its original content is retained read-only.';
    identities.add(identity);
  }
  return null;
}

export function capabilityIdentity(
  key: WorkflowCapabilityReference['key'],
): string {
  return `${key.provider}/${key.kind}/${key.code}/${key.version}`;
}

export function installedReference(
  entry: WorkflowCapabilityCatalogEntry,
): WorkflowCapabilityReference {
  if (
    entry.lifecycle !== 'available' ||
    !entry.input?.hash ||
    !entry.output?.hash ||
    !entry.contractHash
  ) {
    throw new Error(`${entry.key.code} is unavailable for new use.`);
  }
  const reference = {
    key: { ...entry.key },
    inputSchemaHash: entry.input.hash,
    outputSchemaHash: entry.output.hash,
    contractHash: entry.contractHash,
  };
  if (capabilityReferencesShapeError([reference])) throw new Error(`${entry.key.code} is unavailable for new use.`);
  return reference;
}

export function sameCapabilityReference(
  a: WorkflowCapabilityReference,
  b: WorkflowCapabilityReference,
): boolean {
  return (
    capabilityIdentity(a.key) === capabilityIdentity(b.key) &&
    a.inputSchemaHash === b.inputSchemaHash &&
    a.outputSchemaHash === b.outputSchemaHash &&
    a.contractHash === b.contractHash
  );
}

/** Add the exact installed closure without silently repinning an existing selection. Server validation remains authoritative. */
export function addWorkflowCapability(
  document: WorkflowDefinitionDocument,
  selected: WorkflowCapabilityCatalogEntry,
  catalog: readonly WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  if (!supportsWorkflowCapabilities(document.payloadSchemaVersion))
    throw new Error('Upgrade this draft before selecting capabilities.');
  const shapeError = capabilityReferencesShapeError(document.capabilityReferences);
  if (shapeError) throw new Error(shapeError);
  const references = [...(document.capabilityReferences ?? [])];
  const visited = new Set<string>();
  const visiting = new Set<string>();
  function add(reference: WorkflowCapabilityReference): void {
    const id = capabilityIdentity(reference.key);
    if (visiting.has(id))
      throw new Error(
        'The capability dependency declarations contain a cycle.',
      );
    const entries = catalog.filter((e) => capabilityIdentity(e.key) === id);
    if (
      entries.length !== 1 ||
      !sameCapabilityReference(installedReference(entries[0]), reference)
    ) {
      throw new Error(
        `${reference.key.code} has a missing or incompatible installed contract.`,
      );
    }
    const existing = references.filter((r) => capabilityIdentity(r.key) === id);
    if (
      existing.length > 1 ||
      (existing.length === 1 &&
        !sameCapabilityReference(existing[0], reference))
    ) {
      throw new Error(
        `${reference.key.code} already has a different saved reference. Review and remove it before selecting a replacement.`,
      );
    }
    if (visited.has(id)) return;
    visiting.add(id);
    for (const dependency of entries[0].dependencies) add(dependency);
    visiting.delete(id);
    visited.add(id);
    if (!existing.length) references.push(reference);
  }
  add(installedReference(selected));
  if (references.length > 100)
    throw new Error('A workflow can select at most 100 capability references.');
  return { ...document, capabilityReferences: references };
}

export function removeWorkflowCapability(
  document: WorkflowDefinitionDocument,
  reference: WorkflowCapabilityReference,
  catalog: readonly WorkflowCapabilityCatalogEntry[],
): WorkflowDefinitionDocument {
  if (!supportsWorkflowCapabilities(document.payloadSchemaVersion)) throw new Error('This workflow format does not support dependency removal. Its saved references are retained.');
  const shapeError = capabilityReferencesShapeError(document.capabilityReferences);
  if (shapeError) throw new Error(shapeError);
  const id = capabilityIdentity(reference.key);
  const references = document.capabilityReferences ?? [];
  const selected = references.filter(candidate => capabilityIdentity(candidate.key) === id);
  if (selected.length !== 1 || !sameCapabilityReference(selected[0], reference))
    throw new Error('This saved dependency changed or is no longer uniquely selected. Review its current exact reference before removing it.');
  if (
    document.triggers?.some((t) =>
      t?.actions?.some(
        (a) => a?.command?.key && capabilityIdentity(a.command.key) === id,
      ),
    )
  )
    throw new Error(
      'Remove the action declaration first; it requires this capability.',
    );
  const matches = (candidate: WorkflowCapabilityReference | null | undefined) =>
    !!candidate?.key && capabilityIdentity(candidate.key) === id;
  if (
    document.nodes.some(
      (node) =>
        node.declaration &&
        (node.declaration.behavior.commands.some((command) =>
          matches(command.capability),
        ) ||
          matches(node.declaration.detail.mainInteraction?.command) ||
          node.declaration.dashboards?.some(
            (view) => matches(view.view) || view.quickActions.some(matches),
          )),
    )
  )
    throw new Error(
      'Remove the step selection first; it requires this capability.',
    );
  const dependent = references.find(
    (r) =>
      capabilityIdentity(r.key) !== id &&
      catalog.some(
        (e) =>
          capabilityIdentity(e.key) === capabilityIdentity(r.key) &&
          e.dependencies.some((d) => capabilityIdentity(d.key) === id),
      ),
  );
  if (dependent)
    throw new Error(
      `Remove ${dependent.key.code} first; it requires this capability.`,
    );
  return {
    ...document,
    capabilityReferences: references.filter(
      (r) => capabilityIdentity(r.key) !== id,
    ),
  };
}
